import { apiGet, apiPost } from './client.js';
import type { PaginatedMeta } from './tickets.js';
import type { PaymentRecord } from './payments.js';

export type SaleListParams = {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  method?: string;
  staffId?: string;
  status?: string;
  page?: number;
  pageSize?: number;
};

export type SaleStaffRef = {
  id?: string;
  firstName: string;
  lastName: string;
};

export type SaleListItem = {
  id: string;
  saleNumber: string;
  ticketNumber?: string;
  customerId?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  itemCount: number;
  itemSummary?: string | null;
  subtotal: string;
  taxAmount?: string;
  taxTotal?: string;
  total: string;
  amountPaid?: string;
  paidTotal?: string;
  balanceDue: string;
  paymentMethods?: string[];
  status: string;
  hasReturn?: boolean;
  returnTotal?: string | null;
  returnNumber?: string | null;
  createdAt: string;
  createdBy?: SaleStaffRef | null;
  storeId?: string;
};

export type SalesListSummary = {
  salesCount?: number;
  totalRevenue?: string;
  cashTotal?: string;
  cardTotal?: string;
  storeCreditTotal?: string;
};

export type SalesListMeta = PaginatedMeta & {
  summary?: SalesListSummary;
};

export type SaleLineItem = {
  id: string;
  description: string;
  quantity: string | number;
  unitPrice: string;
  discountAmount?: string | null;
  discountPct?: string | null;
  discountReason?: string | null;
  lineTotal: string;
  type?: string;
  inventoryItemId?: string | null;
  returnedQty?: string | number | null;
};

export type SaleReturnItem = {
  lineItemId: string;
  itemName?: string;
  description?: string;
  qty: number;
  unitPrice?: string | number;
  returnToStock: boolean;
  condition: 'good' | 'damaged';
  conditionNotes?: string | null;
};

export type SaleReturn = {
  id: string;
  returnNumber: string;
  refundMethod: string;
  refundAmount: string;
  transactionId?: string | null;
  notes?: string | null;
  status?: string;
  returnItems?: SaleReturnItem[];
  items?: SaleReturnItem[];
  processedBy?: SaleStaffRef | null;
  createdAt: string;
};

export type SaleDetail = {
  id: string;
  saleNumber?: string;
  ticketNumber: string;
  customerId?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  subtotal: string;
  taxTotal?: string;
  taxAmount?: string;
  discountTotal?: string;
  total: string;
  paidTotal?: string;
  amountPaid?: string;
  balanceDue: string;
  status: string;
  hasReturn?: boolean;
  returnTotal?: string | null;
  storeId?: string;
  storeName?: string | null;
  createdAt: string;
  createdBy?: SaleStaffRef | null;
  lineItems: SaleLineItem[];
  payments: PaymentRecord[];
  returns?: SaleReturn[];
};

export type ProcessReturnItemInput = {
  lineItemId: string;
  quantity: number;
  returnToStock: boolean;
  condition: 'good' | 'damaged';
  conditionNotes?: string;
};

export type ProcessReturnInput = {
  items: ProcessReturnItemInput[];
  refundMethod: 'cash' | 'card' | 'store_credit';
  refundAmount: number;
  transactionId?: string;
  notes?: string;
  managerOverrideAuthorizedBy: string;
};

export type ProcessReturnResult = {
  returnNumber: string;
  refundAmount: number | string;
  refundMethod: string;
  itemsRestocked: number;
  itemsNotRestocked: number;
  id?: string;
};

export function listSales(params?: SaleListParams) {
  return apiGet<SaleListItem[], SalesListMeta>('/api/v1/sales', { params });
}

export function getSale(id: string) {
  return apiGet<SaleDetail>(`/api/v1/sales/${id}`).then((res) => res.data);
}

export function processReturn(saleId: string, body: ProcessReturnInput) {
  return apiPost<ProcessReturnResult, ProcessReturnInput>(
    `/api/v1/sales/${saleId}/return`,
    body,
  ).then((res) => res.data);
}
